import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import HourModal from './parts/hour-modal.component';
import Divider from '../../components/divider/divider.component';
import Table from '../../components/table/table.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';
import { setCurrentPage } from '../../redux/entities/hours';
import { getHours, deleteHours } from '../../redux/thunks/hours';
import { useDebounce } from '../../utils/hooks/useDebounce';
import { RowButtons } from '../../components/table/table.styles';
import { FaInfoCircle } from 'react-icons/fa';

const HoursPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const {
    hours,
    currentPage,
    limit,
    totalDocs,
    totalMinutes,
    isUpdating,
    isFetching,
  } = useSelector((state) => state.entities.hours);
  const [showAddModal, setShowAddModal] = useState(false);
  const [hourToEdit, setHourToEdit] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [hourToDelete, setHourToDelete] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  useEffect(() => {
    dispatch(
      getHours({
        page: currentPage,
        limit,
        search: searchValue,
        dateRange,
      })
    );
  }, [currentPage, limit, searchValue, dateRange]);

  useDebounce(
    () => {
      dispatch(setCurrentPage(1));
    },
    [searchValue, dateRange],
    500
  );

  const handleDelete = (ids) => {
    const onSuccess = (message) => {
      enqueueSnackbar(message, { variant: 'success' });
      setHourToDelete(null);
    };
    const onFailure = (message) =>
      enqueueSnackbar(message, { variant: 'error' });

    dispatch(deleteHours({ ids, onSuccess, onFailure }));
  };

  const getTimeString = (totalMinutes) => {
    const hoursAmount = Math.floor(totalMinutes / 60);
    const minutesAmount = totalMinutes % 60;

    const parts = [
      hoursAmount > 0 ? `${hoursAmount} hours` : '',
      hoursAmount > 0 && minutesAmount > 0 ? ' and ' : '',
      minutesAmount > 0 ? `${minutesAmount} minutes` : '',
    ];

    return parts.filter((part) => part && part.length).join(' ');
  };

  const handleEdge = useCallback(() => {
    if (currentPage * limit < totalDocs) {
      dispatch(setCurrentPage(currentPage + 1));
    }
  }, [currentPage, limit, totalDocs]);

  return (
    <>
      {showAddModal && (
        <HourModal show={showAddModal} onClose={() => setShowAddModal(false)} />
      )}

      {hourToEdit && (
        <HourModal
          show={hourToEdit}
          onClose={() => setHourToEdit(null)}
          hour={hourToEdit}
        />
      )}

      <ConfirmModal
        show={hourToDelete}
        onClose={() => setHourToDelete(null)}
        onConfirm={() => handleDelete([hourToDelete])}
      />

      <Table
        title='Hours'
        showAmount={true}
        amount={totalDocs}
        amountTooltip={getTimeString(totalMinutes)}
        headerButtons={
          <>
            <Button
              variant='primary'
              onClick={() => setShowAddModal(true)}
              style={{ width: '-webkit-fill-available' }}
            >
              Add Hour
            </Button>
          </>
        }
        headerItems={[
          { label: 'Project' },
          { label: 'Employee' },
          { label: 'Email' },
          { label: 'Date' },
          { label: 'Started At' },
          { label: 'Ended At' },
          { label: 'Duration' },
          { label: '', style: { flex: '0' } },
          { label: '' },
        ]}
        onSearch={setSearchValue}
        onDateRangeChange={(start, end, label) =>
          setDateRange({
            startDate: start.toISOString(),
            endDate: end.toISOString(),
          })
        }
        onEdge={handleEdge}
        isLoading={isFetching}
      >
        {hours.map((hour, index) => {
          const totalHours = parseFloat(Number(hour.totalMinutes) / 60).toFixed(
            2
          );
          const [hourHours, hourMinutes] = hour.totalTime
            .split(':')
            .map(Number);

          return (
            <>
              <div key={index}>
                <span>{hour?.project?.name || 'Unknown'}</span>
                <span>{hour?.user?.name || 'Unknown'}</span>
                <span>{hour?.user?.email || 'Unknown'}</span>
                <span>
                  {hour?.date
                    ? new Date(hour.date).toISOString().split('T')[0]
                    : 'Unknown'}
                </span>
                <span>{hour.startedAt}</span>
                <span>{hour.finishedAt}</span>
                <span style={{ flex: '0' }}>
                  <OverlayTrigger
                    placement='bottom'
                    overlay={
                      <Tooltip id='tooltip-totalHours'>
                        {hourHours > 0 ? `${hourHours} hours` : ''}
                        {hourHours > 0 && hourMinutes > 0 ? ' and ' : ''}
                        {hourMinutes > 0 ? `${hourMinutes} minutes` : ''}
                      </Tooltip>
                    }
                  >
                    <span>{totalHours} hours</span>
                  </OverlayTrigger>
                </span>
                <span>
                  {hour.notes && (
                    <OverlayTrigger
                      placement='left'
                      overlay={
                        <Tooltip id='tooltip-disabled'>{hour.notes}</Tooltip>
                      }
                    >
                      <span style={{ cursor: 'pointer' }}>
                        <FaInfoCircle color='#17a9d1' fontSize={'1.2rem'} />
                      </span>
                    </OverlayTrigger>
                  )}
                </span>
                <RowButtons>
                  <Button
                    variant='primary'
                    size='sm'
                    onClick={() => setHourToEdit(hour)}
                    disabled={isUpdating[hour._id]}
                  >
                    Edit
                  </Button>
                  <Button
                    variant='danger'
                    size='sm'
                    onClick={() => setHourToDelete(hour._id)}
                    disabled={isUpdating[hour._id]}
                  >
                    Delete
                  </Button>
                </RowButtons>
              </div>
              <Divider />
            </>
          );
        })}
      </Table>
    </>
  );
};

export default HoursPage;
